import Typography from '@mui/material/Typography';
import { useCallback } from 'react';

import RateCardIndicator from '~components/RateCard/RateCardIndicator';
import { Order } from '~hooks/useOrders/models';
import { useSiteRateSchedule } from '~hooks/useSiteRateSchedule/useSiteRateSchedule';

/**
 * The rate-card affordance beside an order's customer rate — nudged onto the text
 * baseline so it sits with the rate it describes rather than riding above it.
 *
 * An order owns no rate cards of its own in the common case; it inherits and matches
 * against its project's set. So the lookup tries the order's own cards first (an order that
 * has been decoupled from its project does own them) and falls back to the project's.
 *
 * The throwing `fetchAll*` variants are load-bearing here: the swallowing `getAll*` ones
 * return an empty list on failure, which this fallthrough would misread as "the order owns
 * no cards" and answer with the project's set. A failure propagates instead, so the
 * indicator shows its failure state rather than the wrong cards.
 */
export default function OrderRateCardIndicator({ order }: { order: Order }) {
  const { fetchAllOrderSiteRateSchedules, fetchAllProjectSiteRateSchedules } =
    useSiteRateSchedule();

  const companyId = order.company?.id;
  const projectId = order.project?.id;

  const loadRateCards = useCallback(async () => {
    const orderOwnedRateCards = companyId
      ? await fetchAllOrderSiteRateSchedules(companyId, order.id)
      : [];

    if (orderOwnedRateCards.length > 0) {
      return orderOwnedRateCards;
    }

    return projectId ? await fetchAllProjectSiteRateSchedules(projectId) : [];
  }, [
    companyId,
    fetchAllOrderSiteRateSchedules,
    fetchAllProjectSiteRateSchedules,
    order.id,
    projectId,
  ]);

  return (
    <Typography
      component="span"
      sx={{
        display: 'inline-flex',
        fontSize: 'inherit',
        ml: 0.5,
        verticalAlign: 'text-bottom',
      }}
    >
      <RateCardIndicator
        appliedRateCardId={order.defaultSiteRateSchedule?.id ?? null}
        loadRateCards={loadRateCards}
      />
    </Typography>
  );
}
